import { z } from "zod";
import { Sha256HexSchema } from "./primitives.js";

export const PoisoningSeveritySchema = z.enum(["info", "low", "medium", "high", "critical"]);
export type PoisoningSeverity = z.infer<typeof PoisoningSeveritySchema>;

/** A single tool-poisoning finding. */
export const PoisoningFindingSchema = z.object({
  ruleId: z.string().min(1),
  severity: PoisoningSeveritySchema,
  toolName: z.string().min(1),
  location: z.enum(["name", "description", "title", "response_template", "input_schema"]),
  /** The matched span (already redacted if it contained secrets). */
  snippet: z.string().min(1),
  rationale: z.string().min(1),
  owaspMcp: z.array(z.string()).default([]),
  owaspLlm: z.array(z.string()).default([])
});
export type PoisoningFinding = z.infer<typeof PoisoningFindingSchema>;

export const HardeningConfigSchema = z.object({
  /** Origin allowlist for the HTTP server. */
  allowedOrigins: z.array(z.string().url()).default([]),
  /** Authorization Server discovery URL (issuer). */
  authorizationServerUrl: z.string().url().optional(),
  /** Canonical resource URI this RS uses for RFC 8707 audience binding. */
  resourceUri: z.string().url().optional(),
  /** JWKS endpoint URL (otherwise we discover from `authorizationServerUrl`/.well-known). */
  jwksUrl: z.string().url().optional(),
  /** Per-deployment PII redaction terms (additional to default rules). */
  extraRedactionTerms: z.array(z.string()).default([]),
  /** Refuse to ship if any finding at-or-above this severity is present. */
  failThreshold: PoisoningSeveritySchema.default("high")
});
/** Post-parse type — every field is populated (defaults applied). */
export type HardeningConfig = z.infer<typeof HardeningConfigSchema>;
/** Caller-facing input type — all default-bearing fields are optional. */
export type HardeningConfigInput = z.input<typeof HardeningConfigSchema>;

export const HardeningReportSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  serverDigest: Sha256HexSchema,
  serverKind: z.enum(["designed", "naive-baseline"]),
  controlsApplied: z.array(z.string()).default([]),
  findings: z.array(PoisoningFindingSchema),
  summary: z.object({
    total: z.number().int().nonnegative(),
    bySeverity: z.record(z.number().int().nonnegative())
  }),
  passed: z.boolean(),
  failThreshold: PoisoningSeveritySchema,
  generatedAt: z.string().datetime({ offset: true })
});
export type HardeningReport = z.infer<typeof HardeningReportSchema>;
